
import React from 'react';
import { useQuery } from "@tanstack/react-query";
import { Link, useNavigate } from "react-router-dom";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { PlusCircle, ArrowRight, Loader2 } from "lucide-react";
import { documentTypeApi } from "@/services/api";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { useAuth } from "@/contexts/AuthContext";

const DocumentTypesSettings = () => {
  const navigate = useNavigate();
  const { selectedRole } = useAuth();

  const { data: allDocumentTypes, isLoading } = useQuery({
    queryKey: ["documentTypes"],
    queryFn: () => documentTypeApi.list(),
  });

  // A management-company context sees the shared platform types plus its
  // own; other companies' types stay out of sight (8-Sep).
  const documentTypes = (allDocumentTypes ?? []).filter((type) => {
    if (selectedRole?.type !== "management_company") return true;
    const mcId = (type as any).management_company_id;
    return mcId == null || Number(mcId) === Number(selectedRole.entityId);
  });

  const handleRowClick = (typeId: number) => {
    navigate(`/backoffice/document-types/${typeId}`);
  };

  return (
    <div className="space-y-6">
      <Card>
        <CardHeader>
          <CardTitle>Document Types</CardTitle>
          <CardDescription>
            Manage document types used throughout the system
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="flex justify-between items-center mb-4">
            <div>
              <p className="text-sm text-muted-foreground">
                Document types define the categories of documents that can be requested and their default settings.
              </p>
            </div>
            <Button asChild>
              <Link to="/backoffice/document-types/new">
                <PlusCircle className="mr-2 h-4 w-4" />
                New Document Type
              </Link>
            </Button>
          </div>

          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Name</TableHead>
                <TableHead>Scope</TableHead>
                <TableHead className="w-[100px]">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {isLoading ? (
                <TableRow>
                  <TableCell colSpan={3} className="text-center">
                    <div className="flex justify-center py-4">
                      <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
                    </div>
                  </TableCell>
                </TableRow>
              ) : documentTypes?.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={3} className="text-center">No document types found</TableCell>
                </TableRow>
              ) : (
                documentTypes?.map((type) => (
                  <TableRow
                    key={type.id}
                    className="cursor-pointer"
                    onClick={() => handleRowClick(type.id)}
                  >
                    <TableCell className="font-medium">{type.name}</TableCell>
                    {/* null = platform-wide type; set = one management
                        company's own (8-Sep restructure, phase 1). */}
                    <TableCell>
                      <span className="inline-block rounded-full border px-2 py-0.5 text-xs text-muted-foreground">
                        {(type as any).management_company_id
                          ? `MC #${(type as any).management_company_id}`
                          : "Platform"}
                      </span>
                    </TableCell>
                    <TableCell onClick={(e) => e.stopPropagation()}>
                      <Button variant="ghost" size="sm" asChild>
                        <Link to={`/backoffice/document-types/${type.id}`}>
                          <ArrowRight className="h-4 w-4" />
                        </Link>
                      </Button>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </CardContent>
      </Card>
    </div>
  );
};

export default DocumentTypesSettings;
